import { Controller } from "@hotwired/stimulus"

// Generischer Keyboard-Shortcut-Trigger fuer beliebige Elemente.
//
// Modi:
//   1. Modifier-Combo: keyValue = "alt+q", "meta+k", "ctrl+shift+s"
//      → triggert sofort wenn alle Modifiers + Key passen.
//   2. Sequenz (Linear/GitHub-Style): keyValue = "g n", "g a"
//      → "g" gedrueckt, dann innerhalb 1.2s die zweite Taste.
//
// Aktion: das Trigger-Element wird via .click() ausgeloest.
//         keine Wirkung wenn Fokus in editbarem Feld liegt.
export default class extends Controller {
  static values = {
    key:   String,
    event: String
  }

  connect() {
    this.boundHandle = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandle)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandle)
    if (this._sequenceTimer) clearTimeout(this._sequenceTimer)
  }

  handleKeydown(event) {
    if (this._isEditableTarget(event.target)) return
    const spec = this._parseKey(this.keyValue)
    if (!spec) return

    if (spec.kind === "combo") {
      if (this._matchesCombo(event, spec)) {
        event.preventDefault()
        this.element.click()
      }
      return
    }

    if (spec.kind === "sequence") {
      this._handleSequence(event, spec)
    }
  }

  // --- internals ---

  _parseKey(value) {
    if (!value) return null
    const v = value.trim().toLowerCase()
    if (v.includes(" ")) {
      const parts = v.split(/\s+/)
      if (parts.length === 2) return { kind: "sequence", first: parts[0], second: parts[1] }
      return null
    }
    if (v.includes("+")) {
      const parts = v.split("+").map(p => p.trim())
      const key = parts.pop()
      const mods = new Set(parts)
      return {
        kind: "combo",
        key,
        alt:   mods.has("alt") || mods.has("option"),
        ctrl:  mods.has("ctrl"),
        meta:  mods.has("meta") || mods.has("cmd"),
        shift: mods.has("shift")
      }
    }
    // einfache Einzel-Taste ohne Modifier
    return { kind: "combo", key: v, alt: false, ctrl: false, meta: false, shift: false }
  }

  _matchesCombo(event, spec) {
    return event.key.toLowerCase() === spec.key &&
           event.altKey   === spec.alt &&
           event.ctrlKey  === spec.ctrl &&
           event.metaKey  === spec.meta &&
           event.shiftKey === spec.shift
  }

  _handleSequence(event, spec) {
    const k = event.key.toLowerCase()
    // Keine Modifier zulassen — Sequenzen sind plain-letter
    if (event.altKey || event.ctrlKey || event.metaKey) return

    if (this._sequenceArmed) {
      clearTimeout(this._sequenceTimer)
      this._sequenceArmed = false
      if (k === spec.second) {
        event.preventDefault()
        this.element.click()
      }
      return
    }

    if (k === spec.first) {
      event.preventDefault()
      this._sequenceArmed = true
      this._sequenceTimer = setTimeout(() => { this._sequenceArmed = false }, 1200)
    }
  }

  _isEditableTarget(el) {
    if (!el) return false
    const tag = el.tagName
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true
    if (el.isContentEditable) return true
    return false
  }
}
